/**
 * Cloupak 前端 API 客户端
 *
 * 封装与服务端（Hono on Cloudflare Workers）通信的 fetch 调用。
 * 包含后端 snake_case → 前端 camelCase 的字段映射和状态值转换。
 */

import { getLang, translate, type TranslationKey } from '../i18n';

/** 用户信息（与后端 User 实体对应） */
export interface User {
  id: string;
  email: string;
  name: string;
  plan: string;
}

/** 应用构建状态（前端统一使用） */
export type BuildStatus = 'idle' | 'uploading' | 'building' | 'success' | 'failed';

/** 订阅信息（前端格式） */
export interface SubscriptionInfo {
  id: string;
  plan: string;
  status: string;
  provider: string;
  amount: number;
  currency: string;
  interval: string;
  currentPeriodStart?: string | null;
  currentPeriodEnd?: string | null;
  cancelAtPeriodEnd: boolean;
  canceledAt?: string | null;
  startedAt: string;
}

/** 套餐信息 */
export interface PlanInfo {
  id: string;
  priceCents: number;
  currency: string;
  interval: string;
  maxApps: number;
  maxBuildsPerDay: number;
  hasWatermark: boolean;
  storageMb: number;
  multiDeviceSync: boolean;
}

/** 应用信息（前端统一格式） */
export interface BuildInfo {
  id: string;
  status: BuildStatus;
  createdAt: string;
  errorMessage?: string | null;
}

export interface AppInfo {
  id: string;
  name: string;
  status: BuildStatus;
  createdAt: string;
  lastBuildAt?: string | null;
  apkUrl?: string | null;
  progress?: number;
  currentStep?: string | null;
  buildLog?: string | null;
  errorMessage?: string | null;
  recentBuilds?: BuildInfo[];
}

/** API 访问令牌信息（不含明文，明文仅创建时返回一次） */
export interface ApiTokenInfo {
  id: string;
  name: string;
  createdAt: string;
  lastUsedAt?: string | null;
}

/** 通用后端错误 */
export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

const baseUrl = import.meta.env.VITE_API_BASE_URL ?? '';

const TOKEN_STORAGE_KEY = 'cloupak_token';

/** 注入鉴权头：从 localStorage 读取 token，存在则附加 Authorization */
function buildHeaders(extra?: HeadersInit): HeadersInit {
  const headers: Record<string, string> = {};
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  if (extra) {
    const source = extra instanceof Headers
      ? Object.fromEntries(extra.entries())
      : (extra as Record<string, string>);
    Object.assign(headers, source);
  }
  return headers;
}

/**
 * 将后端错误响应转为当前语言的文案
 *
 * 优先用 error code 映射 apiError.code.* 翻译（后端 error 字段是中文），
 * 未收录的 code 回退原始 error 文本
 */
function localizeApiError(
  data: { error?: unknown; message?: unknown; detail?: unknown; code?: unknown } | null,
  fallback: string,
): string {
  const raw = String(data?.error ?? data?.message ?? data?.detail ?? '') || fallback;
  const code = typeof data?.code === 'string' ? data.code : '';
  if (!code) return raw;
  const key = `apiError.code.${code}` as TranslationKey;
  const localized = translate(getLang(), key);
  return localized !== key ? localized : raw;
}

/** 统一的请求封装，自动处理 JSON / 文本与错误转换 */
async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${baseUrl}${path}`, {
    ...init,
    headers: buildHeaders(init?.headers),
  });

  if (!res.ok) {
    const fallback = translate(getLang(), 'apiError.requestFailed', { status: res.status });
    let message = fallback;
    try {
      const data = await res.json();
      message = localizeApiError(data, fallback);
    } catch {
      // 响应体非 JSON，忽略解析错误
    }
    throw new ApiError(message, res.status);
  }

  const contentType = res.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    return (await res.json()) as T;
  }
  return (await res.text()) as unknown as T;
}

// ============================================================
// 后端响应 → 前端格式映射
// ============================================================

/** 后端状态值 → 前端状态值 */
function mapStatus(backendStatus: string): BuildStatus {
  const map: Record<string, BuildStatus> = {
    draft: 'idle',
    building: 'building',
    deployed: 'success',
    success: 'success',
    failed: 'failed',
    pending: 'building',
    idle: 'idle',
    uploading: 'uploading',
  };
  return map[backendStatus] ?? 'idle';
}

/** 后端 App 原始格式（snake_case） */
interface RawApp {
  id: string;
  user_id?: string;
  name: string;
  slug?: string;
  status: string;
  r2_prefix?: string;
  created_at?: string;
  createdAt?: string;
  updated_at?: string;
  updatedAt?: string;
  last_build_at?: string | null;
  lastBuildAt?: string | null;
}

/** 将后端原始 App 对象映射为前端 AppInfo */
function mapApp(raw: RawApp): AppInfo {
  return {
    id: raw.id,
    name: raw.name,
    status: mapStatus(raw.status),
    createdAt: raw.created_at ?? raw.createdAt ?? '',
    lastBuildAt: raw.last_build_at ?? raw.lastBuildAt ?? null,
  };
}

export const api = {
  /** 注册新用户 */
  async register(email: string, password: string, name: string, turnstileToken?: string): Promise<{ token: string; user: User }> {
    return request<{ token: string; user: User }>('/api/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name, turnstileToken }),
    });
  },

  /** 登录 */
  async login(email: string, password: string, turnstileToken?: string): Promise<{ token: string; user: User }> {
    return request<{ token: string; user: User }>('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, turnstileToken }),
    });
  },

  /** 修改密码（需登录，验证当前密码） */
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await request<{ success: boolean }>('/api/auth/change-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  },

  /** 忘记密码（发送重置邮件；无论邮箱是否存在都返回相同响应） */
  async forgotPassword(email: string, turnstileToken?: string): Promise<void> {
    await request<{ success: boolean }>('/api/auth/forgot-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, turnstileToken }),
    });
  },

  /** 重置密码（凭邮件中的令牌，成功后自动登录） */
  async resetPassword(
    token: string,
    newPassword: string,
  ): Promise<{ token: string; user: User }> {
    return request<{ token: string; user: User }>('/api/auth/reset-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, newPassword }),
    });
  },

  /** 获取当前登录用户信息（需要 Authorization 头） */
  async getMe(): Promise<User> {
    const data = await request<{ user: User }>('/api/auth/me');
    return data.user;
  },

  /** 获取应用列表 */
  async getApps(): Promise<AppInfo[]> {
    const data = await request<RawApp[] | { apps: RawApp[] }>('/api/apps');
    const apps = Array.isArray(data) ? data : data.apps;
    return apps.map(mapApp);
  },

  /** 获取单个应用详情（含最近构建记录） */
  async getApp(appId: string): Promise<AppInfo> {
    const data = await request<{ app: RawApp; recentBuilds: { id: string; status: string; created_at: string; error_message?: string | null }[] }>(`/api/apps/${encodeURIComponent(appId)}`);
    const latest = data.recentBuilds?.[0];
    return {
      ...mapApp(data.app),
      errorMessage: data.app.status === 'failed' ? latest?.error_message ?? null : null,
      buildLog: data.app.status === 'failed' ? latest?.error_message ?? null : null,
      recentBuilds: (data.recentBuilds ?? []).map((build) => ({
        id: build.id,
        status: mapStatus(build.status),
        createdAt: build.created_at,
        errorMessage: build.error_message,
      })),
    };
  },

  /** 创建应用（仅建立记录，返回待上传的 appId） */
  async createApp(name: string): Promise<AppInfo> {
    const raw = await request<{ appId: string; name: string; slug: string; status: string; appToken: string; r2Prefix: string; createdAt: string }>('/api/apps', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    });
    return {
      id: raw.appId,
      name: raw.name,
      status: mapStatus(raw.status),
      createdAt: raw.createdAt,
    };
  },

  /** 上传 ZIP 项目包（multipart/form-data，浏览器自动设置 boundary） */
  async uploadZip(appId: string, file: File): Promise<void> {
    const form = new FormData();
    form.append('file', file);
    await request<void>(`/api/apps/${encodeURIComponent(appId)}/upload`, {
      method: 'POST',
      body: form,
    });
  },

  /** 触发构建（上传完成后调用） */
  async triggerBuild(appId: string): Promise<AppInfo> {
    // 后端返回 {buildId, appId, status, message}，这里转换为 AppInfo
    const raw = await request<{ buildId: string; appId: string; status: string; message: string }>(`/api/apps/${encodeURIComponent(appId)}/build`, {
      method: 'POST',
    });
    return {
      id: raw.appId,
      name: '',
      status: mapStatus(raw.status),
      createdAt: '',
    };
  },

  /** 修改应用名称（APK 内应用名需重新构建后才会更新） */
  async renameApp(appId: string, name: string): Promise<void> {
    await request<void>(`/api/apps/${encodeURIComponent(appId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    });
  },

  /** 删除应用（连同 R2 文件和数据库记录一起清除） */
  async deleteApp(appId: string): Promise<void> {
    await request<void>(`/api/apps/${encodeURIComponent(appId)}`, {
      method: 'DELETE',
    });
  },

  /**
   * 下载 APK 文件
   * 通过 API 端点下载（需要 JWT 认证），避免依赖 *.apps.cloupak.app 域名。
   * 成功后自动触发浏览器下载。
   * @throws ApiError 如果 APK 不存在或下载失败
   */
  async downloadApk(appId: string): Promise<void> {
    const res = await fetch(`${baseUrl}/api/apps/${encodeURIComponent(appId)}/apk`, {
      headers: buildHeaders(),
    });

    if (!res.ok) {
      const fallback = translate(getLang(), 'apiError.downloadFailed', { status: res.status });
      let message = fallback;
      try {
        const data = await res.json();
        message = localizeApiError(data, fallback);
      } catch {
        // 响应体非 JSON
      }
      throw new ApiError(message, res.status);
    }

    // 将响应转为 Blob 并触发下载
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${appId}.apk`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  // ============================================================
  // 计费与订阅
  // ============================================================

  /** 获取当前订阅状态 */
  async getSubscription(): Promise<{ plan: string; subscription: SubscriptionInfo | null }> {
    const data = await request<{
      plan: string;
      subscription: {
        id: string;
        plan: string;
        status: string;
        provider: string;
        amount: number;
        currency: string;
        interval: string;
        current_period_start?: string | null;
        current_period_end?: string | null;
        cancel_at_period_end: boolean;
        canceled_at?: string | null;
        started_at: string;
      } | null;
    }>('/api/billing/subscription');

    return {
      plan: data.plan,
      subscription: data.subscription
        ? {
            id: data.subscription.id,
            plan: data.subscription.plan,
            status: data.subscription.status,
            provider: data.subscription.provider,
            amount: data.subscription.amount,
            currency: data.subscription.currency,
            interval: data.subscription.interval,
            currentPeriodStart: data.subscription.current_period_start,
            currentPeriodEnd: data.subscription.current_period_end,
            cancelAtPeriodEnd: data.subscription.cancel_at_period_end,
            canceledAt: data.subscription.canceled_at,
            startedAt: data.subscription.started_at,
          }
        : null,
    };
  },

  /** 获取套餐列表和可用支付渠道 */
  async getPlans(): Promise<{
    plans: PlanInfo[];
    alipay: { pro: { priceCents: number; currency: string } };
    channels: string[];
  }> {
    const data = await request<{
      plans: Array<{
        id: string;
        price_cents: number;
        currency: string;
        interval: string;
        max_apps: number;
        max_builds_per_day: number;
        has_watermark: boolean;
        storage_mb: number;
        multi_device_sync: boolean;
      }>;
      alipay: { pro: { price_cents: number; currency: string } };
      channels: string[];
    }>('/api/billing/plans');

    return {
      plans: data.plans.map((p) => ({
        id: p.id,
        priceCents: p.price_cents,
        currency: p.currency,
        interval: p.interval,
        maxApps: p.max_apps,
        maxBuildsPerDay: p.max_builds_per_day,
        hasWatermark: p.has_watermark,
        storageMb: p.storage_mb,
        multiDeviceSync: p.multi_device_sync,
      })),
      alipay: {
        pro: {
          priceCents: data.alipay.pro.price_cents,
          currency: data.alipay.pro.currency,
        },
      },
      channels: data.channels,
    };
  },

  /** 创建支付宝订单，返回二维码 URL */
  async createAlipayOrder(
    planId: string,
    returnUrl?: string,
  ): Promise<{ orderId: string; qrUrl: string; amount: number; currency: string }> {
    const data = await request<{
      order_id: string;
      qr_url: string;
      amount: number;
      currency: string;
    }>('/api/billing/alipay/create', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ plan: planId, return_url: returnUrl }),
    });
    return {
      orderId: data.order_id,
      qrUrl: data.qr_url,
      amount: data.amount,
      currency: data.currency,
    };
  },

  /** 查询支付宝订单状态（轮询用） */
  async checkAlipayStatus(orderId: string): Promise<'pending' | 'succeeded' | 'failed'> {
    const data = await request<{ status: string }>(
      `/api/billing/alipay/status/${encodeURIComponent(orderId)}`,
    );
    return data.status as 'pending' | 'succeeded' | 'failed';
  },

  /** 创建虎皮椒订单（微信/支付宝扫码，一次性购买 30 天） */
  async createXunhupayOrder(
    planId: string,
    channel: 'wxpay' | 'alipay',
    returnUrl?: string,
  ): Promise<{ orderId: string; qrUrl: string; payUrl: string; amount: number; currency: string }> {
    const data = await request<{
      order_id: string;
      qr_url: string;
      pay_url: string;
      amount: number;
      currency: string;
    }>('/api/billing/xunhupay/create', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ plan: planId, channel, return_url: returnUrl }),
    });
    return {
      orderId: data.order_id,
      qrUrl: data.qr_url,
      payUrl: data.pay_url,
      amount: data.amount,
      currency: data.currency,
    };
  },

  /** 查询虎皮椒订单状态（轮询用） */
  async checkXunhupayStatus(
    orderId: string,
    channel: 'wxpay' | 'alipay',
  ): Promise<'pending' | 'succeeded' | 'failed'> {
    const data = await request<{ status: string }>(
      `/api/billing/xunhupay/status/${encodeURIComponent(orderId)}?channel=${channel}`,
    );
    return data.status as 'pending' | 'succeeded' | 'failed';
  },

  /** 创建 Waffo Checkout，返回跳转 URL */
  async createWaffoCheckout(
    planId: string,
    successUrl?: string,
    cancelUrl?: string,
  ): Promise<{ checkoutUrl: string }> {
    const data = await request<{ checkout_url: string; session_id: string }>(
      '/api/billing/waffo/checkout',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan: planId,
          success_url: successUrl,
          cancel_url: cancelUrl,
        }),
      },
    );
    return { checkoutUrl: data.checkout_url };
  },

  /** 取消订阅（到期失效） */
  async cancelSubscription(subscriptionId?: string): Promise<{ success: boolean }> {
    return request<{ success: boolean }>('/api/billing/cancel', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ subscription_id: subscriptionId }),
    });
  },

  // ============================================================
  // API 访问令牌（PAT）— 供 agent / CLI 等自动化工具使用
  // ============================================================

  /** 创建 API 令牌（明文仅返回这一次） */
  async createApiToken(name: string): Promise<ApiTokenInfo & { token: string }> {
    const raw = await request<{ id: string; name: string; token: string; created_at?: string; createdAt?: string }>(
      '/api/tokens',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      },
    );
    return {
      id: raw.id,
      name: raw.name,
      createdAt: raw.created_at ?? raw.createdAt ?? '',
      token: raw.token,
    };
  },

  /** 列出 API 令牌 */
  async getApiTokens(): Promise<ApiTokenInfo[]> {
    const data = await request<{ tokens: Array<{ id: string; name: string; last_used_at?: string; created_at?: string }> }>(
      '/api/tokens',
    );
    return (data.tokens ?? []).map((t) => ({
      id: t.id,
      name: t.name,
      lastUsedAt: t.last_used_at ?? null,
      createdAt: t.created_at ?? '',
    }));
  },

  /** 撤销 API 令牌 */
  async revokeApiToken(tokenId: string): Promise<void> {
    await request<{ success: boolean }>(`/api/tokens/${encodeURIComponent(tokenId)}`, {
      method: 'DELETE',
    });
  },
};

/**
 * 构建 APK 下载 API URL（通过 Workers API 域名，不依赖 *.apps.cloupak.app）
 */
export function buildApkUrl(appId: string): string {
  return `${baseUrl}/api/apps/${encodeURIComponent(appId)}/apk`;
}

/**
 * 拼接应用部署后的在线访问链接。
 * Workers 通配路由为 *.cloupak.app（APPS_SUBDOMAIN），应用域名是 {appId}.cloupak.app，
 * 可通过 VITE_APPS_DOMAIN 覆盖。
 */
export function buildAccessUrl(appId: string): string {
  const domain = import.meta.env.VITE_APPS_DOMAIN ?? 'cloupak.app';
  return `https://${appId}.${domain}`;
}

/**
 * 构建免认证的 APK 公开下载 URL（供手机扫码使用，无需登录态）。
 */
export function buildPublicApkUrl(appId: string): string {
  return `${baseUrl}/api/apps/${encodeURIComponent(appId)}/apk-public`;
}
